'use client';
/*
 * Documentation:
 * Alert — https://app.subframe.com/6fc7f5fbb098/library?component=Alert_3a65613d-d546-467c-80f4-aaba6a7edcd5
 */

import React from 'react';
import { FeatherInfo } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';

type AlertVariant = 'brand' | 'neutral' | 'error' | 'success' | 'warning';

interface AlertRootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  tone?: 'outline' | 'filled';
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

const FILLED_CLASS: Record<AlertVariant, string> = {
  brand: 'bg-brand-50 border-brand-200',
  neutral: 'bg-neutral-50 border-neutral-200',
  error: 'bg-error-50 border-error-200',
  success: 'bg-success-50 border-success-200',
  warning: 'bg-warning-50 border-warning-200',
};

const AlertRoot = React.forwardRef<HTMLDivElement, AlertRootProps>(function AlertRoot(
  {
    variant = 'neutral',
    tone = 'outline',
    icon = <FeatherInfo />,
    title,
    description,
    actions,
    className,
    ...otherProps
  }: AlertRootProps,
  ref,
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        'group/3a65613d flex w-full flex-col items-start gap-2 rounded-[12px] border border-solid p-4',
        tone === 'filled' ? FILLED_CLASS[variant] : 'border-neutral-200 bg-white shadow-sm',
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex w-full items-start gap-3">
        {icon ? (
          <div
            className={SubframeUtils.twClassNames(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              {
                'bg-warning-50 text-warning-600': variant === 'warning',
                'bg-success-50 text-success-600': variant === 'success',
                'bg-error-50 text-error-600': variant === 'error',
                'bg-brand-50 text-brand-600': variant === 'brand',
                'bg-neutral-50 text-neutral-600': variant === 'neutral',
              },
            )}
          >
            <SubframeCore.IconWrapper className="text-[18px]">{icon}</SubframeCore.IconWrapper>
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col items-start pt-0.5">
          {title ? (
            <span className="text-body-bold font-body-bold text-default-font w-full leading-tight">
              {title}
            </span>
          ) : null}
          {description ? (
            <div className="text-caption font-caption text-subtext-color mt-1 w-full leading-normal">
              {description}
            </div>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-start justify-end">{actions}</div> : null}
      </div>
    </div>
  );
});

export const Alert = AlertRoot;
